import { useEffect, type RefObject } from 'react'

/**
 * Suggestion ref configuration for dropdowns
 */
interface SuggestionRef {
    ref: RefObject<HTMLElement>
    setter: (value: boolean) => void
}

/**
 * Options for useModalBehavior hook
 */
interface UseModalBehaviorOptions {
    modalRef: RefObject<HTMLElement>
    onClose: () => void
    suggestionRefs?: SuggestionRef[]
}

/**
 * Custom hook for common modal/dropdown behavior
 * 
 * Handles:
 * - Click outside to close
 * - Escape key to close
 * - Click outside suggestion dropdowns
 * 
 * @example
 * const modalRef = useRef<HTMLDivElement>(null)
 * const publisherInputRef = useRef<HTMLInputElement>(null)
 * 
 * useModalBehavior({
 *   modalRef,
 *   onClose,
 *   suggestionRefs: [
 *     { ref: publisherInputRef, setter: setShowPublisherSuggestions },
 *     { ref: locationInputRef, setter: setShowLocationSuggestions }
 *   ]
 * })
 */
export function useModalBehavior({ modalRef, onClose, suggestionRefs = [] }: UseModalBehaviorOptions): void {
    // Handle click outside modal to close
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [modalRef, onClose])

    // Handle escape key to close
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [onClose])

    // Handle click outside suggestion dropdowns
    useEffect(() => {
        if (suggestionRefs.length === 0) return

        const handleClick = (e: MouseEvent) => {
            suggestionRefs.forEach(({ ref, setter }) => {
                if (ref.current && !ref.current.contains(e.target as Node)) {
                    setter(false)
                }
            })
        }

        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [suggestionRefs])
}
